import { useEffect } from "react";

// In iOS the keyboard can resize and pan the visual viewport independently of
// the layout viewport. Only anchor the phone ticket shell while its composer
// is focused and a substantial keyboard-sized reduction is actually visible.
export default function useTicketKeyboardViewport(shellRef, enabled, isPhonePwa = false) {
  useEffect(() => {
    const shell = shellRef.current;
    const viewport = window.visualViewport;
    if (!enabled || !shell || !viewport) return undefined;

    let frame = 0;
    let baselineHeight = Math.max(window.innerHeight || 0, viewport.height || 0);
    let baselineWidth = viewport.width || window.innerWidth || 0;
    let followLatest = false;

    const reset = () => {
      shell.removeAttribute("data-ticket-keyboard");
      shell.style.removeProperty("--ticket-keyboard-height");
      shell.style.removeProperty("--ticket-keyboard-top");
    };
    const isComposerFocused = () => {
      const active = document.activeElement;
      return Boolean(active && shell.contains(active) && active.matches("[data-ticket-message-input]"));
    };
    const getMessages = () => shell.querySelector("[data-ticket-message-list]");
    const isNearLatest = (list) => Boolean(list && list.clientHeight > 0 &&
      Math.abs(list.scrollTop) <= 80);
    const onFocusIn = (event) => {
      if (event.target?.matches?.("[data-ticket-message-input]")) {
        followLatest = isNearLatest(getMessages());
      }
      schedule();
    };
    const onListInteraction = (event) => {
      if (event.target?.closest?.("[data-ticket-message-list]")) followLatest = false;
    };
    const sync = () => {
      frame = 0;
      const height = Number(viewport.height);
      const width = Number(viewport.width);
      const offset = Number(viewport.offsetTop);
      const scale = Number(viewport.scale || 1);
      if (!Number.isFinite(height) || height <= 0 || !Number.isFinite(offset) || !Number.isFinite(scale) || Math.abs(scale - 1) > 0.01) {
        reset();
        return;
      }
      if (width && Math.abs(width - baselineWidth) > 50) {
        baselineWidth = width;
        baselineHeight = Math.max(window.innerHeight || 0, height);
      }
      const focused = isComposerFocused();
      if (!focused) baselineHeight = Math.max(baselineHeight, height, window.innerHeight || 0);
      if (!focused || baselineHeight - height <= 8) {
        reset();
        return;
      }
      shell.style.setProperty("--ticket-keyboard-height", `${height}px`);
      // In installed phone PWAs WebKit can report a transient large offset as
      // it pans the page; adding it to a fixed shell visibly drops the header.
      shell.style.setProperty("--ticket-keyboard-top", `${isPhonePwa ? 0 : Math.max(0, offset)}px`);
      shell.setAttribute("data-ticket-keyboard", "");
      const messages = getMessages();
      if (followLatest && messages) {
        messages.scrollTop = 0;
      }
    };
    const schedule = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(sync);
    };

    if (isComposerFocused()) followLatest = isNearLatest(getMessages());
    sync();
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", schedule);
    shell.addEventListener("touchstart", onListInteraction, { passive: true });
    shell.addEventListener("wheel", onListInteraction, { passive: true });
    viewport.addEventListener("resize", schedule);
    viewport.addEventListener("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", schedule);
      shell.removeEventListener("touchstart", onListInteraction);
      shell.removeEventListener("wheel", onListInteraction);
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      reset();
    };
  }, [shellRef, enabled, isPhonePwa]);
}
